/* eslint react-hooks/exhaustive-deps: "warn" */

import { useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { CompareMatrix } from 'ui/molecules/CompareMatrix/CompareMatrix';
import type { CompareMatrixColumn, CompareMatrixRow } from 'ui/molecules/CompareMatrix/CompareMatrix.types';
import { Section } from 'ui/molecules/Section/Section';

import styles from './CompanyDataCompare.module.scss';

type Translate = (key: string) => string;

const getColumns = (t: Translate): CompareMatrixColumn[] => [
  { name: 'Global Database', tag: t('Direct from registries'), highlight: true },
  { name: 'Dun & Bradstreet', tag: t('Licensed network') },
  { name: "Moody's", tag: t('Licensed providers') },
  { name: 'Sayari', tag: t('Mixed collection') },
];

const getRows = (t: Translate): CompareMatrixRow[] => [
  {
    metric: t('Where the data comes from'),
    cells: [
      {
        text: t(
          '400+ government registries and tax authorities, connected to individually. No data partners, no web crawling, no self-reported records.',
        ),
      },
      {
        text: t(
          'Largely licensed and curated. Tens of thousands of sources: a worldwide partner network, public records, court filings, web crawling and crowd-sourced or self-reported records.',
        ),
      },
      {
        text: t(
          "Licensed. 170+ information providers plus Moody's own sources and regulatory sources and published annual reports, standardised into one database.",
        ),
      },
      {
        text: t(
          'Mixed. Collects directly from registries, customs bureaus and regulatory agencies across 250+ jurisdictions, alongside trade and shipping records.',
        ),
      },
    ],
  },
  {
    metric: t('Steps between the filing and your agent'),
    cells: [
      {
        text: t(
          'One. The register, then us, then you. We read the filing and return the value with its register and filing reference. No partner, reseller or modelled substitute in the chain.',
        ),
      },
      {
        text: t(
          'Several. Filing to a Worldwide Network partner or web source, re-indexed into the Data Cloud under a D-U-N-S Number, modelled values added, then to you.',
        ),
      },
      {
        text: t(
          "Several. Filing to one of 170+ information providers or Moody's own collection, standardised into Orbis, then to you.",
        ),
      },
      {
        text: t(
          'Two. Filing collected and stored as the original document, passed through entity resolution into the graph, then to you. Source document linked.',
        ),
      },
    ],
  },
  {
    metric: t('Provenance depth'),
    cells: [
      { text: t('Register, filing reference, retrieval timestamp and document link on each value returned.') },
      { text: t('Record level. D-U-N-S is the anchor rather than the filing.') },
      { text: t('Blends multiple sources into one record. Filing-level attribution not stated publicly.') },
      { text: t('Links responses to primary documents with a confidence assertion.') },
    ],
  },
  {
    metric: t('Identifier model'),
    cells: [
      {
        text: t(
          'Resolves on registration number, VAT number, name or domain. No proprietary identifier to adopt or licence.',
        ),
      },
      { text: t('D-U-N-S number, proprietary.') },
      { text: t('BvD ID, proprietary.') },
      { text: t('Sayari entity ID, proprietary.') },
    ],
  },
  {
    metric: t('MCP server and connectors'),
    cells: [
      {
        text: t(
          'Yes. Remote MCP server, with connectors for Claude, OpenAI and Microsoft Copilot. mcp.globaldatabase.com',
        ),
      },
      { text: t('Connectors into Microsoft Copilot, OpenAI and IBM watsonx.') },
      { text: t('Published on Databricks Marketplace and Claude for Financial Services.') },
      { text: t('World Model MCP.') },
    ],
  },
  {
    metric: t('Redistribution rights'),
    cells: [
      {
        text: t(
          'Available. Serve the data to your own customers, inside your product or through your agent, under a redistribution licence with the terms written into the contract.',
        ),
      },
      {
        text: t(
          'Restricted. Onward distribution is limited by the terms of the partners and third-party sources the data is licensed from.',
        ),
      },
      {
        text: t(
          'Restricted. Onward distribution is limited by the terms of the information providers the data is licensed from.',
        ),
      },
      { text: t('Restricted by contract.') },
    ],
  },
  {
    metric: t('AI training rights'),
    cells: [
      {
        text: t(
          'Granted. The dataset can be licensed for use in model training and retrieval, with the scope set out in the contract.',
        ),
      },
      {
        text: t(
          'Not offered. Model-training use is excluded by the terms of the partners and third-party sources the data is licensed from.',
        ),
      },
      {
        text: t(
          'Not offered. Model-training use is excluded by the terms of the information providers the data is licensed from.',
        ),
      },
      { text: t('Not offered publicly.') },
    ],
  },
];

export const CompanyDataCompare = () => {
  const { t } = useInt();
  const frameRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(frameRef, { threshold: 0.3 });

  return (
    <Section
      className={styles.root}
      title={t('Every major provider now has an MCP server. The difference is where the data came from.')}
      description={t(
        "Dun & Bradstreet, Moody's and Sayari all ship agent access and all describe their data as sourced. What separates them is how many hands the filing passed through before it reached your agent, and whether it can still be traced back.",
      )}
    >
      <div className={styles.glowBlue} aria-hidden="true" />
      <div className={styles.glowIndigo} aria-hidden="true" />

      <div className={styles.frameWrap}>
        <div ref={frameRef} className={cn(styles.frame, revealed && styles.frameShine)}>
          <CompareMatrix columns={getColumns(t)} rows={getRows(t)} />
        </div>
      </div>
    </Section>
  );
};
